/* assets/setup-builder-ui.js
 * Viperia Setup Builder, Figma redesign (Oct 2026). Presentation only: never
 * posts to the basket, never changes the tier data or what gets added (that is
 * assets/setup-builder.js). Every feature checks for its markup and does nothing
 * without it; with JavaScript off the pages still work (all tiers stacked, cards
 * are links, the group navigation is plain links).
 *
 * Detail page (sections/species-setup-detail.liquid):
 * - Tier picker: the cards from snippets/setup-tier-card.liquid become a tab
 *   list (ARIA tabs, automatic activation, arrow / Home / End keys). One tier is
 *   shown at a time; the others get the hidden attribute. Starts with the tier in
 *   the URL hash, else the highlighted card, else the first.
 * - Wholesale: a card's [data-setup-pick-total] copies its tier's BSS total
 *   ([data-setup-total]) whenever setup-builder.js shows it.
 * - Popular extras: when extras are ticked, every tier's total panel gets a line
 *   with the extras, and (retail only) the Total and RRP include them, so the
 *   total matches what the button adds. Unticking all restores the Liquid text
 *   exactly. Wholesale totals come from BSS, so they are left alone and the
 *   line shows the count only.
 * - Optional item toggles (Oct 2026): an unticked optional row is dimmed
 *   (setup-item--excluded) and, for retail, comes off the Total, RRP and Saving,
 *   the tier card price and its "Save N%" (data-setup-save-template); "Without
 *   optional extras" hides while no optional item is included. What is added is
 *   decided by assets/setup-builder.js from the same checkboxes.
 * Landing page (sections/setups-landing.liquid):
 * - Group tabs [data-setups-nav]: smooth scroll (none with reduced motion) and
 *   aria-current on the group in view.
 * - Group dropdown [data-setups-select]: jumps to the chosen group and swaps
 *   the round icon.
 */
(() => {
  'use strict';

  const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Same placeholders as Shopify's money filter (subset used by UK shops).
  function formatMoney(cents, format) {
    const fmt = String(format || '').replace(/<[^>]*>/g, '');
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const group = (decimals, thousands, point) => {
      const [whole, frac] = (cents / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? point + frac : '');
    };
    if (!match) return `£${group(2, ',', '.')}`;
    const amounts = {
      amount: () => group(2, ',', '.'),
      amount_no_decimals: () => group(0, ',', '.'),
      amount_with_comma_separator: () => group(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => group(0, '.', ','),
      amount_with_apostrophe_separator: () => group(2, "'", '.'),
      amount_with_space_separator: () => group(2, ' ', ','),
    };
    return fmt.replace(match[0], (amounts[match[1]] || amounts.amount)());
  }

  // ---- Tier picker -------------------------------------------------------

  function initPicker(wrap) {
    if (wrap.dataset.setupPickerReady) return;
    const list = wrap.querySelector('[data-setup-picker-list]');
    if (!list) return;
    const cards = Array.from(list.querySelectorAll('a[data-setup-pick]'))
      .map((card) => ({ card, panel: document.getElementById(card.dataset.setupPickTarget) }))
      .filter((c) => c.panel);
    if (!cards.length) return;
    wrap.dataset.setupPickerReady = 'true';
    list.setAttribute('role', 'tablist');
    Array.from(list.children).forEach((li) => li.setAttribute('role', 'presentation'));
    cards.forEach(({ card, panel }) => {
      card.setAttribute('role', 'tab');
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', card.id);
    });

    const select = (index, focus) => {
      cards.forEach(({ card, panel }, i) => {
        const on = i === index;
        card.setAttribute('aria-selected', on ? 'true' : 'false');
        card.tabIndex = on ? 0 : -1;
        card.classList.toggle('setup-pick--selected', on);
        panel.hidden = !on;
      });
      if (focus) cards[index].card.focus({ preventScroll: true });
      // Phones: the card row scrolls sideways; bring the chosen card into view
      // without moving the page.
      if (list.scrollWidth > list.clientWidth + 1) {
        const item = cards[index].card.parentElement;
        const left = item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2;
        list.scrollTo({ left: Math.max(0, left), behavior: focus === undefined || reduceMotion() ? 'auto' : 'smooth' });
      }
    };

    const fromHash = cards.findIndex(({ panel }) => window.location.hash === `#${panel.id}`);
    const featured = cards.findIndex(({ card }) => card.hasAttribute('data-setup-pick-featured'));
    let start = 0;
    if (fromHash >= 0) start = fromHash;
    else if (featured >= 0) start = featured;
    select(start);
    // A link to a tier (#SetupTier-...) elsewhere on the page opens its tab.
    window.addEventListener('hashchange', () => {
      const i = cards.findIndex(({ panel }) => window.location.hash === `#${panel.id}`);
      if (i >= 0) select(i, false);
    });

    cards.forEach(({ card }, i) => {
      card.addEventListener('click', (event) => {
        event.preventDefault();
        select(i, false);
      });
      card.addEventListener('keydown', (event) => {
        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % cards.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (i - 1 + cards.length) % cards.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = cards.length - 1;
        else if (event.key === ' ') next = i;
        if (next === null) return;
        event.preventDefault();
        select(next, true);
      });
    });

    // Wholesale: copy the tier's BSS total onto its card when it is shown.
    cards.forEach(({ card, panel }) => {
      const target = card.querySelector('[data-setup-pick-total]');
      const box = panel.querySelector('[data-setup-wholesale-total]');
      if (!target || !box) return;
      const sync = () => {
        const total = box.querySelector('[data-setup-total]');
        target.textContent = !box.hidden && total ? total.textContent.trim() : '';
      };
      sync();
      if (typeof MutationObserver === 'function') {
        new MutationObserver(sync).observe(box, { attributes: true, attributeFilter: ['hidden'], childList: true, characterData: true, subtree: true });
      }
    });
  }

  // ---- Totals: optional item toggles + popular extras ------------------------

  // One pass over every tier: unticked optional rows (toggles enabled by
  // assets/setup-builder.js) come off the retail Total, RRP, Saving and the tier
  // card; ticked popular extras are added to the Total and RRP (not the card,
  // which is the tier's own price) and listed on a line. "Without optional
  // extras" is hidden while no optional item is included. When nothing is
  // unticked or ticked, every text goes back to the Liquid text exactly.
  // Wholesale totals come from BSS (assets/setup-builder.js), so wholesale
  // footers only get the extras line with the count.
  const keepText = (el) => {
    if (el && el.dataset.setupBaseText === undefined) el.dataset.setupBaseText = el.textContent;
    return el;
  };
  const int = (v) => {
    const n = parseInt(v, 10);
    return Number.isInteger(n) ? n : 0;
  };

  function initTotals(root) {
    if (root.dataset.setupTotalsReady) return;
    const box = root.querySelector('[data-setup-extras]');
    const inputs = box ? Array.from(box.querySelectorAll('input[data-setup-extra]')) : [];
    const hasToggles = !!root.querySelector('input[data-setup-optional]');
    const hasQty = !!root.querySelector('input[data-setup-qty], select[data-setup-variant]');
    if (!inputs.length && !hasToggles && !hasQty) return;
    root.dataset.setupTotalsReady = 'true';
    if (box) box.dataset.setupExtrasReady = 'true';
    const label = (box && box.dataset.extrasLabel) || 'Popular extras';

    const update = () => {
      const ticked = inputs.filter((el) => el.checked && !el.disabled);
      const sum = ticked.reduce((a, el) => a + int(el.dataset.price), 0);
      root.querySelectorAll('[data-setup-tier]').forEach((tierEl) => {
        // Unticked optional rows.
        let exLine = 0;
        let exRrp = 0;
        let optionalIn = 0;
        // SUP-32: quantity changes vs the setup quantity (retail pence).
        let qtyDelta = 0;
        let rrpDelta = 0;
        let requiredDelta = 0;
        const footerEl = tierEl.querySelector('.setup-tier__footer');
        const rowFormat = (footerEl && footerEl.dataset.moneyFormat) || (box && box.dataset.moneyFormat) || '';
        tierEl.querySelectorAll('.setup-item[data-item-status]').forEach((row) => {
          const toggle = row.querySelector('input[data-setup-optional]');
          const qtyInput = row.querySelector('input[data-setup-qty]');
          const baseQty = int(row.dataset.baseQuantity) || 1;
          let q = baseQty;
          if (qtyInput) {
            const min = Math.max(1, int(qtyInput.min) || 1);
            const max = Math.max(min, int(qtyInput.max) || 99);
            q = Math.min(max, Math.max(min, int(qtyInput.value) || min));
          }
          const unit = int(row.dataset.unitPrice);
          const unitRrp = int(row.dataset.unitRrp) || unit;
          // TKT-1400 a colour select can change the unit price too.
          const sel = row.querySelector('select[data-setup-variant]');
          const baseLine = int(row.dataset.linePrice);
          const baseRrpLine = int(row.dataset.lineRrpBase) || baseLine;
          if ((qtyInput || sel) && row.dataset.unitPrice !== undefined) {
            const lineEl = row.querySelector('[data-setup-row-line]');
            if (lineEl) lineEl.textContent = formatMoney(unit * q, rowFormat);
            const rrpEl = row.querySelector('[data-setup-row-rrp]');
            if (rrpEl) rrpEl.textContent = formatMoney(unitRrp * q, rowFormat);
            const each = row.querySelector('.setup-item__price [data-setup-each]');
            if (each) each.hidden = q < 2;
          }
          const included = !toggle || toggle.checked;
          if (toggle) row.classList.toggle('setup-item--excluded', !toggle.checked);
          if (included) {
            if (toggle) optionalIn += 1;
            const d = unit * q - baseLine;
            qtyDelta += d;
            if (row.dataset.required !== 'false') requiredDelta += d;
            rrpDelta += unitRrp * q - baseRrpLine;
            return;
          }
          exLine += int(row.dataset.linePrice);
          exRrp += int(toggle.dataset.lineRrp || row.dataset.linePrice);
        });
        // Unticked rows leave the total at their setup quantity (as rendered);
        // ticked rows add the change from that quantity.
        exLine -= qtyDelta;
        exRrp -= rrpDelta;
        const changed = exLine !== 0 || exRrp !== 0 || qtyDelta !== 0;
        const footer = tierEl.querySelector('.setup-tier__footer');
        if (!footer) return;
        const retail = footer.dataset.priceBasis === 'inc_vat';
        const format = footer.dataset.moneyFormat || (box && box.dataset.moneyFormat) || '';

        let line = footer.querySelector('[data-setup-extras-line]');
        if (!ticked.length) {
          if (line) line.remove();
        } else {
          if (!line) {
            line = document.createElement('p');
            line.className = 'setup-tier__extras';
            line.setAttribute('data-setup-extras-line', '');
            line.append(document.createElement('span'), document.createElement('span'));
            line.lastChild.className = 'price';
            const anchor = footer.querySelector('.setup-tier__subtotal-alt') || footer.querySelector('.setup-tier__subtotal');
            if (anchor) anchor.after(line);
            else footer.prepend(line);
          }
          line.firstChild.textContent = `${label} (${ticked.length})`;
          line.lastChild.textContent = retail ? `+${formatMoney(sum, format)}` : '';
        }
        if (!retail) return;

        const alt = footer.querySelector('.setup-tier__subtotal-alt');
        if (alt) {
          alt.hidden = optionalIn === 0;
          const altAmount = keepText(alt.querySelector('.price'));
          if (altAmount) altAmount.textContent = requiredDelta ? formatMoney(int(footer.dataset.requiredSubtotal) + requiredDelta, format) : altAmount.dataset.setupBaseText;
        }
        const base = int(footer.dataset.tierSubtotal) - exLine;
        const amount = keepText(footer.querySelector('.setup-tier__subtotal-amount'));
        if (amount) amount.textContent = changed || sum ? formatMoney(base + sum, format) : amount.dataset.setupBaseText;

        const rrp = footer.querySelector('[data-setup-rrp]');
        const saving = footer.querySelector('[data-setup-saving]');
        let rrpBase = base;
        if (rrp) {
          rrpBase = int(rrp.dataset.setupRrp) - exRrp;
          const rrpAmount = keepText(rrp.querySelector('.price'));
          if (rrpAmount) rrpAmount.textContent = changed || sum ? formatMoney(rrpBase + sum, format) : rrpAmount.dataset.setupBaseText;
          rrp.hidden = rrpBase <= base;
        }
        if (saving) {
          const savingAmount = keepText(saving.querySelector('.price'));
          if (savingAmount) savingAmount.textContent = changed ? formatMoney(Math.max(0, rrpBase - base), format) : savingAmount.dataset.setupBaseText;
          saving.hidden = rrpBase <= base;
        }

        // The tier card: price = tier total without extras, saving in whole %.
        const card = tierEl.id ? root.querySelector(`[data-setup-pick-target="${tierEl.id}"]`) : null;
        const item = card && card.closest('.setup-picker__item');
        if (!item) return;
        const price = keepText(card.querySelector('.setup-pick__price'));
        if (price) price.textContent = changed ? formatMoney(base, format) : price.dataset.setupBaseText;
        const save = keepText(item.querySelector('[data-setup-save-template]'));
        if (save) {
          const percent = rrpBase > base && rrpBase > 0 ? Math.floor(((rrpBase - base) * 100) / rrpBase) : 0;
          save.textContent = changed ? save.dataset.setupSaveTemplate.replace('[percent]', percent) : save.dataset.setupBaseText;
          save.hidden = percent <= 0;
        }
      });
      inputs.forEach((el) => el.closest('.setup-extra') && el.closest('.setup-extra').classList.toggle('setup-extra--ticked', el.checked));
    };
    inputs.forEach((el) => el.addEventListener('change', update));
    root.addEventListener('change', (event) => {
      if (event.target && event.target.matches && event.target.matches('input[data-setup-optional], input[data-setup-qty], select[data-setup-variant]')) update();
    });
    // SUP-32: quantity +/- buttons and typing (setup-builder.js has already clamped).
    root.addEventListener('setup-builder:change', update);
    root.addEventListener('input', (event) => {
      if (event.target && event.target.matches && event.target.matches('input[data-setup-qty]')) update();
    });
    // A restored page (back button) can come back with boxes changed.
    update();
    window.addEventListener('pageshow', update);
  }

  // ---- Landing page group navigation -----------------------------------------

  function scrollToGroup(id) {
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function initLanding(nav) {
    if (nav.dataset.setupsNavReady) return;
    nav.dataset.setupsNavReady = 'true';
    const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
    const setCurrent = (id) => links.forEach((a) => {
      if (a.getAttribute('href') === `#${id}`) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
    links.forEach((a) => a.addEventListener('click', (event) => {
      const id = a.getAttribute('href').slice(1);
      if (!document.getElementById(id)) return;
      event.preventDefault();
      setCurrent(id);
      scrollToGroup(id);
      if (window.history && window.history.replaceState) window.history.replaceState(null, '', `#${id}`);
    }));
    const groups = links.map((a) => document.getElementById(a.getAttribute('href').slice(1))).filter(Boolean);
    if (groups.length && typeof IntersectionObserver === 'function') {
      const seen = new Map();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => seen.set(e.target.id, e.isIntersecting ? e.boundingClientRect.top : null));
        const visible = groups.filter((g) => seen.get(g.id) !== null && seen.get(g.id) !== undefined);
        if (visible.length) setCurrent(visible[0].id);
      }, { rootMargin: '-20% 0px -60% 0px' });
      groups.forEach((g) => io.observe(g));
    }
  }

  function initSelect(selectEl) {
    if (selectEl.dataset.setupsSelectReady) return;
    selectEl.dataset.setupsSelectReady = 'true';
    const wrap = selectEl.closest('[data-setups-select-wrap]');
    if (wrap) wrap.hidden = false;
    const icon = wrap ? wrap.querySelector('[data-setups-select-icon]') : null;
    const syncIcon = () => {
      const opt = selectEl.options[selectEl.selectedIndex];
      if (!icon || !opt) return;
      const src = opt.dataset.icon || '';
      icon.hidden = !src;
      if (src) icon.src = src;
    };
    syncIcon();
    selectEl.addEventListener('change', () => {
      syncIcon();
      if (selectEl.value) scrollToGroup(selectEl.value);
    });
  }

  const init = (scope) => {
    const s = scope || document;
    s.querySelectorAll('.setup-tiers').forEach(initPicker);
    s.querySelectorAll('[data-setup-builder]').forEach(initTotals);
    s.querySelectorAll('[data-setups-nav]').forEach(initLanding);
    s.querySelectorAll('select[data-setups-select]').forEach(initSelect);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
